import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, CalendarDays, ArrowRight } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate } from "@/lib/format";

const TYPE_COLORS = {
  task: "bg-primary/15 text-primary border-primary/30",
  deliverable: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  project: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
  renewal: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
};

const TYPE_LABEL = { task: "Task", deliverable: "Deliverable", project: "Project deadline", renewal: "Service renewal" };
const TYPE_DEST = { task: "/tasks", deliverable: "/deliverables", project: "/projects", renewal: "/finance" };

function iso(d) { return d.toISOString().slice(0, 10); }
function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }

export default function CalendarPage() {
  const nav = useNavigate();
  const [now, setNow] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);

  const { year, month, startDay, totalDays, start, end } = useMemo(() => {
    const y = now.getFullYear(); const m = now.getMonth();
    const startDay = new Date(y, m, 1).getDay();
    const totalDays = daysInMonth(y, m);
    return { year: y, month: m, startDay, totalDays, start: iso(new Date(y, m, 1)), end: iso(new Date(y, m, totalDays)) };
  }, [now]);

  useEffect(() => { api.get("/calendar", { params: { start, end } }).then((r) => setEvents(r.data)).catch(() => {}); }, [start, end]);

  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] ||= []).push(e); });
    return map;
  }, [events]);

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));

  const monthName = now.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const today = iso(new Date());

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Timeline</div>
          <h1 className="text-4xl font-black tracking-tight">{monthName}</h1>
          <p className="text-sm text-muted-foreground mt-1">Tasks · Deliverables · Projects · Renewals — click any event to open.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="cal-prev-btn" variant="outline" onClick={() => setNow(new Date(year, month - 1, 1))}><ChevronLeft size={14} /></Button>
          <Button variant="outline" onClick={() => setNow(new Date())}><CalendarDays size={14} className="mr-1" />Today</Button>
          <Button data-testid="cal-next-btn" variant="outline" onClick={() => setNow(new Date(year, month + 1, 1))}><ChevronRight size={14} /></Button>
        </div>
      </div>

      <div className="border border-border rounded-md overflow-hidden bg-card">
        <div className="grid grid-cols-7 border-b border-border bg-muted/50">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="p-2 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((c, i) => {
            const key = c ? iso(c) : `empty-${i}`;
            const evs = c ? (byDate[iso(c)] || []) : [];
            const isToday = c && iso(c) === today;
            return (
              <div key={key} className={`min-h-28 border-t border-l border-border p-1.5 ${isToday ? "bg-primary/5" : ""} ${(i + 1) % 7 === 0 ? "border-r" : ""}`}>
                {c && (
                  <>
                    <div className={`text-xs font-mono ${isToday ? "text-primary font-bold" : "text-muted-foreground"}`}>{c.getDate()}</div>
                    <div className="space-y-1 mt-1">
                      {evs.slice(0, 3).map((e) => (
                        <button
                          key={e.id}
                          onClick={() => setSelected(e)}
                          data-testid={`cal-event-${e.id}`}
                          className={`w-full text-left text-[10px] px-1.5 py-0.5 rounded border truncate transition-colors hover:brightness-110 ${TYPE_COLORS[e.type] || "bg-muted"}`}
                          title={e.title}
                        >
                          {e.title}
                        </button>
                      ))}
                      {evs.length > 3 && (
                        <button
                          onClick={() => setSelected({ _multi: true, date: iso(c), events: evs })}
                          className="text-[9px] font-mono text-muted-foreground hover:text-foreground"
                          data-testid={`cal-more-${iso(c)}`}
                        >+{evs.length - 3} more</button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground font-mono">
        {Object.entries(TYPE_COLORS).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1"><span className={`inline-block w-3 h-3 rounded border ${v}`} />{k}</div>
        ))}
      </div>

      {/* Event detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="sm:max-w-md">
          {selected?._multi ? (
            <>
              <DialogHeader>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Events on</div>
                <DialogTitle>{fmtDate(selected.date)}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {selected.events.map((ev) => (
                  <button
                    key={ev.id}
                    onClick={() => setSelected(ev)}
                    className="w-full text-left border border-border rounded-md p-3 hover:border-primary/60 transition-colors"
                    data-testid={`cal-multi-item-${ev.id}`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <div className="text-[10px] font-mono uppercase text-muted-foreground">{TYPE_LABEL[ev.type]}</div>
                        <div className="font-semibold">{ev.title}</div>
                      </div>
                      {ev.status && <StatusBadge value={ev.status} />}
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : selected ? (
            <>
              <DialogHeader>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{TYPE_LABEL[selected.type] || selected.type}</div>
                <DialogTitle>{selected.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between border-b border-border pb-2">
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">Date</span>
                  <span>{fmtDate(selected.date)}</span>
                </div>
                {selected.status && (
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="font-mono text-[10px] uppercase text-muted-foreground">Status</span>
                    <StatusBadge value={selected.status} />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
                <Button
                  data-testid="cal-detail-open"
                  onClick={() => { setSelected(null); nav(TYPE_DEST[selected.type] || "/dashboard"); }}
                  className="bg-primary hover:bg-primary/90"
                >
                  Open {TYPE_LABEL[selected.type]?.toLowerCase() || "detail"} <ArrowRight size={14} className="ml-1" />
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
